import { BybButton } from "@Components/common/byb-ds";
import { ListItemBase, ListItemCell, ListItemLayout } from "@Components/game/common";
import BybDialog from "@Components/game/common/dialog/byb-vr-dialog";
import Icon from "@Components/generic/icon";
import { UnityBridge } from "@Context/common/general/context";
import { faEdit, faPlay, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { useApi, useGlobalContext } from "@hooks";
import useI18n from "@i18n";
import { Box, Stack, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Api } from "@Shared/schemas";
import { PlaylistResponse } from "@Shared/schemas/playlists/playlistSchemas";
import { format } from "date-fns";
import { useState } from "react";

interface PlaylistListItemProps {
  playlist: PlaylistResponse;
  onRefresh?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const StyledListItem = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.dsSurface.v100,
  cursor: "pointer",
  "&:hover": {
    backgroundColor: theme.palette.dsSurface.v200,
  },
}));

const PlaylistListItem = ({ playlist, onRefresh, onEdit, onDelete }: PlaylistListItemProps) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { globalState } = useGlobalContext();
  const scenarioCount = playlist.scenario_count ?? 0;
  // Setup session creation API
  const { call: createSession } = useApi<typeof Api.Playlists.CreatePlaylistSession>({
    api: Api.Playlists.CreatePlaylistSession,
    request: {
      playlist_id: playlist.id,
      account_id: globalState.account?.id! as number,
    },
  });

  const onPlaylistSessionCreated = (data: any) => {
    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.StartPlaylist,
      payload: {
        items: data.play_items,
        play_session_id: data.play_session_id,
      },
    });
  };

  const handlePlay = async () => {
    const { data } = await createSession();
    onPlaylistSessionCreated(data);
  };

  const handleDelete = () => {
    onDelete?.();
    setDeleteDialogOpen(false);
  };

  return (
    <Box
      sx={{
        padding: isSmDown ? theme.spacing(2) : theme.spacing(2, 4),
      }}
    >
      <ListItemBase>
        <ListItemLayout columns={8}>
          {/* Play button as a full circle on far left */}
          <ListItemCell first colSpan={2}>
            <Stack direction="row" spacing={2}>
              <BybButton
                size="small"
                color="primary"
                variant="contained"
                icon={<Icon icon={faPlay} />}
                onClick={handlePlay}
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  padding: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
              <Stack spacing={0}>
                <Typography variant="textBase600">{playlist.name}</Typography>
                <Typography variant="textS500" color="text.secondary">
                  {playlist.description}
                </Typography>
              </Stack>
            </Stack>
          </ListItemCell>

          {/* Date fields */}
          <ListItemCell colSpan={1}>
            <Typography variant="textBase500">{playlist.start_at ? format(new Date(playlist.start_at), "dd.MM.yy") : "N/A"}</Typography>
          </ListItemCell>
          <ListItemCell colSpan={1}>
            <Typography variant="textBase500">{playlist.end_at ? format(new Date(playlist.end_at), "dd.MM.yy") : "N/A"}</Typography>
          </ListItemCell>

          {/* Scenarios count */}
          <ListItemCell colSpan={1}>
            <Typography variant="textBase500" align="center">
              {scenarioCount}
            </Typography>
          </ListItemCell>

          {/* Status */}
          <ListItemCell colSpan={1}>
            <Typography
              variant="textBase500"
              align="center"
              sx={{
                color: playlist.published ? theme.palette.success.main : theme.palette.warning.main,
              }}
            >
              {playlist.published ? i18n("playlists.playlist.list.item.status.published") : i18n("playlists.playlist.list.item.status.draft")}
            </Typography>
          </ListItemCell>

          {/* Updated date */}
          <ListItemCell colSpan={1}>
            <Typography variant="textBase500">{playlist.updated_at ? format(new Date(playlist.updated_at), "dd.MM.yy") : "N/A"}</Typography>
          </ListItemCell>

          {/* Action buttons */}
          <ListItemCell colSpan={1}>
            <Stack direction="row" spacing={1}>
              <BybButton
                size="small"
                color="secondary"
                variant="outlined"
                icon={<Icon icon={faEdit} />}
                onClick={onEdit}
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  padding: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
              <BybButton
                size="small"
                color="secondary"
                variant="outlined"
                icon={<Icon icon={faTrash} />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  minWidth: "40px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  padding: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: theme.palette.error.main,
                  borderColor: theme.palette.error.main,
                  "&:hover": {
                    borderColor: theme.palette.error.main,
                    backgroundColor: theme.palette.error.light,
                  },
                }}
              />
            </Stack>
          </ListItemCell>
        </ListItemLayout>
      </ListItemBase>

      {/* Delete confirmation dialog */}
      <BybDialog
        name="delete-playlist-dialog"
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Playlist"
        dialogActions={
          <>
            <BybButton onClick={() => setDeleteDialogOpen(false)} color="secondary" fullWidth>
              {i18n("cancel")}
            </BybButton>
            <BybButton
              onClick={handleDelete}
              color="secondary"
              fullWidth
              sx={{
                color: theme.palette.error.main,
                borderColor: theme.palette.error.main,
                "&:hover": {
                  borderColor: theme.palette.error.main,
                  backgroundColor: theme.palette.error.light,
                },
              }}
            >
              {i18n("delete")}
            </BybButton>
          </>
        }
      >
        <Typography variant="textBase500">{i18n("playlists.playlist.list.item.delete-confirmation.message", { name: playlist.name })}</Typography>
      </BybDialog>
    </Box>
  );
};

export default PlaylistListItem;
